/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
/* eslint-disable max-len */

import { zodResolver } from '@hookform/resolvers/zod';
import { DefaultEventsMap } from '@socket.io/component-emitter';
import debounce from 'lodash.debounce';
import {
    Edit2,
    Link2,
    Copy,
    BookOpen,
    MonitorCog,
    SquareDashedMousePointer,
    ArrowLeft,
    PanelRight,
    Save,
    Videotape,
    FileText,
    Files,
} from 'lucide-react';
import { generateSlug } from 'random-word-slugs';
import { CSSProperties, useEffect, useReducer, useState, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import Split from 'react-split';
import { io, Socket } from 'socket.io-client';
import { v4 as uuid } from 'uuid';
import * as z from 'zod';

import { InstructionsTab } from './InstructionsTab';
import { LiveAgentBlockCard } from './LiveAgentBlockCard';
import { LiveAgentPlayer } from './LiveAgentPlayer';
import { LiveAgentRemoteControl } from './LiveAgentRemoteControl';
import { LiveAgentRunInformationPopover } from './LiveAgentRunInformationPopover';
import {
    createSeparatorStep,
    dedupeActions,
    getSeparatorPairs,
    isStepExecutable,
    trimHovers,
} from './LiveAgentSessionUtils';
import { LiveAgentSteps } from './LiveAgentSteps';
import { Step, RecordedAction, Story } from './LiveAgentTypes';
import { Modal } from './Modal';
import { Tabs } from './Tabs';

import { LiveSession } from '@/commons/commons';
import { SelectorKey, TargetingStrategy } from '@/commons/data';
import { TestSuite } from '@/commons/protocols';
import { Button } from '@/components/atoms/Button';
import { formatError } from '@/components/atoms/Error';
import { LiveAgentEditor } from '@/components/codeEditor/LiveAgentEditor';
import ErrorBoundary from '@/components/ErrorBoundary';
import TooltipOverParent from '@/components/sessionRecorder/TooltipOverParent';
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import Spinner from '@/components/ui/Spinner';
import { toast } from '@/components/ui/use-toast';
import { useGet, useMutate } from '@/net/commons';
import { cn } from '@/utils';

const liveAgentSchema = z.object({
    goal: z.string().min(1, { message: 'The agent needs a non empty goal.' }),
    entryHref: z.string().url({ message: 'The URL format is invalid. Make sure to enter a valid URL.' }),
    ticket_url: z.union([z.literal(''), z.string().url().optional()]),
    block: z.boolean().optional(),
    steps: z.array(z.any()),
});

export type LiveAgentSchema = z.infer<typeof liveAgentSchema>;

export type LiveAgentSessionProps = {
    isEditing?: boolean;
};

const getHighlighterCountMessage = (highlighterCount: number) => {
    if (highlighterCount === 0) {
        return 'No elements found';
    }
    if (highlighterCount === 1) {
        return 'Selector is unique';
    }
    return `${highlighterCount} elements found, please make the selector more specific in order to be able to run the step`;
};

const groupByHash = (steps: Step[]) => {
    const groups: { intentHash?: string | null; intentPrompt?: string | null; items: Step[] }[] = [];
    for (const item of steps) {
        let lastGroup = groups.at(-1);
        if (lastGroup === undefined || lastGroup.intentHash !== item.intentHash) {
            lastGroup = { intentHash: item.intentHash, intentPrompt: item.intentPrompt, items: [item] };
            groups.push(lastGroup);
        } else {
            lastGroup.items.push(item);
        }
    }
    return groups;
};

const getEscapeHatchActions = (step: Step) => {
    const selector = step.target as string;
    let selectors: RecordedAction['selectors'] = {
        fullQualifiedSelector: {
            selector,
            unique: true,
        },
    };

    // only send and store the selectors that the user has actually chosen
    // Please keep this mapping in sync with getSelectorKeysFromEscapeHatch
    switch (step.targetingStrategy) {
        case 'xpath':
            selectors = {
                xPath: {
                    selector,
                    unique: true,
                },
                fullXPath: {
                    selector,
                    unique: true,
                },
            };
            break;
        case 'datatestid':
            selectors = {
                'data-testid': {
                    selector,
                    unique: true,
                },
            };
            break;
        case 'arialabel':
            selectors = {
                'aria-label': {
                    selector,
                    unique: true,
                },
            };
            break;
        case 'css':
        case 'ai':
        default:
            selectors = {
                fullQualifiedSelector: {
                    selector,
                    unique: true,
                },
            };
            break;
        case 'playwright':
            selectors = {
                playwrightCode: {
                    selector,
                    unique: true,
                },
            };
            break;
    }
    return [
        {
            startTime: -1,
            result: 'PASS',
            kind: 'webAgent',
            hasPending: false,
            name: step.action,
            value: step.value,
            typingMode: step.typingMode,
            selectors,
        },
    ];
};

/**
 * This function returns the list of selectors that are set for a given targeting strategy from escape hatch.
 */
export const getSelectorKeysFromEscapeHatch = function (targetingStrategy: TargetingStrategy): SelectorKey[] {
    // Please keep this mapping in sync with getEscapeHatchActions
    switch (targetingStrategy) {
        case 'xpath':
            return ['xPath', 'fullXPath'];
        case 'datatestid':
            return ['data-testid'];
        case 'arialabel':
            return ['aria-label'];
        case 'css':
            return ['fullQualifiedSelector'];
        case 'playwright':
            return ['playwrightCode'];
    }
    throw new Error(`trying to generate a single selector for unsupported targeting strategy: ${targetingStrategy}`);
};

export const LiveAgentSession = ({ isEditing }: LiveAgentSessionProps) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const blockWorkflow = searchParams.get('block') !== null;
    const onPremRunId = searchParams.get('runid') || '';
    const endpoint = searchParams.get('endpoint') || '';
    const editMode = searchParams.get('mode') || '';
    const [drafting, setDrafting] = useState(false);
    isEditing = isEditing || editMode === 'edit';

    const [openStep, setOpenStep] = useState<string | 'all' | null>(isEditing ? null : 'all');
    const [viewMode, setViewMode] = useState<'list' | 'editor'>('list');
    const [isShiftKeyHeld, setIsShiftKeyHeld] = useState(false);
    const editorRef = useRef<any>(null);

    const updateOpenStep = (stepId: string, force: boolean = false) => {
        if (!isEditing) {
            return;
        }
        if (force) {
            setOpenStep(stepId);
            return;
        }
        if (openStep === stepId) {
            setOpenStep(null);
        } else {
            setOpenStep(stepId);
        }
    };

    // unfortunately need to keep this reducer here because of the need of one side effect when adding steps
    const stepReducer = (state: Step[], action: any) => {
        const stepId = uuid();
        switch (action.type) {
            case 'add':
                if (state.findIndex((step) => step.kind === 'empty') !== -1) {
                    return state.map((step) => {
                        if (step.kind === 'empty') {
                            updateOpenStep(stepId, true);
                            return { ...action.payload, id: stepId };
                        }
                        return step;
                    });
                }
                updateOpenStep(stepId, true);
                return [...state, { ...action.payload, id: stepId }];
            case 'addAtIndex':
                updateOpenStep(stepId, true);
                return [
                    ...state.slice(0, action.payload.index),
                    { ...action.payload.data, id: stepId },
                    ...state.slice(action.payload.index),
                ];
            case 'insertEmptyStep':
                const tempState = state.map((step) => {
                    if (step.kind === 'empty') {
                        return { ...step, kind: 'toBeDeleted' };
                    }
                    return step;
                });
                updateOpenStep(stepId, true);
                return [
                    ...tempState.slice(0, action.payload.index + 1),
                    { kind: 'empty', id: stepId },
                    ...tempState.slice(action.payload.index + 1),
                ].filter((step) => step.kind !== 'toBeDeleted');
            case 'duplicate':
                updateOpenStep(stepId, true);
                return state.flatMap((step, index) => {
                    if (index === action.payload.index) {
                        const stepDuplicate = {
                            ...step,
                            executed: false,
                            actions: undefined,
                            recorded: undefined,
                            result: undefined,
                            code: undefined,
                            id: stepId,
                        };
                        if (stepDuplicate.blockSteps) {
                            stepDuplicate.blockSteps = (step.blockSteps || [])?.map((blockStep) => ({
                                ...blockStep,
                                executed: false,
                                actions: undefined,
                                recorded: undefined,
                                result: undefined,
                                code: undefined,
                                id: uuid(),
                            }));
                        }
                        return [step, stepDuplicate];
                    }
                    return step;
                });
            case 'remove':
                if (action.payload.parentIndex !== undefined) {
                    return state.map((step: Step, i: number) =>
                        i === action.payload.parentIndex
                            ? {
                                  ...step,
                                  blockSteps: step.blockSteps?.filter((_, j: number) => j !== action.payload.index),
                              }
                            : step,
                    );
                }
                return state.filter((_: Step, i: number) => i !== action.payload.index);
            case 'update':
                if (action.payload.parentIndex !== undefined) {
                    return state.map((step: Step, i: number) => {
                        if (i === action.payload.parentIndex) {
                            return {
                                ...step,
                                blockSteps: step.blockSteps?.map((blockStep, j) =>
                                    j === action.payload.index ? { ...blockStep, ...action.payload.data } : blockStep,
                                ),
                            };
                        }
                        return step;
                    });
                }
                // Reset the code for the updated step if its specific properties are being updated
                const needReset = Object.keys(action.payload.data).some(
                    (key) =>
                        ![
                            'executed',
                            'running',
                            'result',
                            'actions',
                            'childActions',
                            'pwCode',
                            'mediaAssets',
                            'targetingStrategy',
                        ].includes(key),
                );
                return state.map((step: Step, i: number) =>
                    i === action.payload.index
                        ? {
                              ...step,
                              ...action.payload.data,
                              ...(needReset ? { code: undefined } : {}),
                          }
                        : step,
                );
            case 'reorder':
                const { from, to } = action.payload;
                const newState = [...state];
                if (action.payload.parentIndex !== undefined) {
                    const parent = newState[action.payload.parentIndex];
                    const [removed] = (parent.blockSteps || []).splice(from, 1);
                    parent.blockSteps?.splice(to, 0, removed);
                    return newState;
                }
                const [removed] = newState.splice(from, 1);
                newState.splice(to, 0, removed);
                return newState;
            case 'addAction':
                return state.map((step: Step, i: number) =>
                    i === action.payload.index
                        ? {
                              ...step,
                              actions: dedupeActions([...(step.actions || []), ...action.payload.data]),
                          }
                        : step,
                );
            case 'removeAction':
                return state.map((step: Step, i: number) =>
                    i === action.payload.index
                        ? {
                              ...step,
                              actions: (step.actions || []).filter(
                                  (_: any, j: number) => j !== action.payload.actionIndex,
                              ),
                              // do the same with recorded if it exists
                              recorded: (step.recorded || []).filter(
                                  (_: any, j: number) => j !== action.payload.actionIndex,
                              ),
                          }
                        : step,
                );
            case 'updateAction':
                return state.map((step: Step, i: number) =>
                    i === action.payload.index
                        ? {
                              ...step,
                              actions: (step.actions || []).map((stepAction: any, j: number) =>
                                  j === action.payload.actionIndex ? action.payload.data : stepAction,
                              ),
                          }
                        : step,
                );
            case 'addDescription':
                return state.map((step: Step) => {
                    if (step.kind === 'block') {
                        step.blockSteps = step.blockSteps?.map((blockStep) => {
                            if (blockStep.kind !== 'recordedActions') {
                                return blockStep;
                            }
                            return {
                                ...blockStep,
                                actions: (blockStep.actions || []).map((recordedAction) => {
                                    if (recordedAction.hid === action.payload.hid) {
                                        return { ...recordedAction, targetDescription: action.payload.description };
                                    }
                                    return recordedAction;
                                }),
                            };
                        });
                    }
                    if (step.kind !== 'recordedActions') {
                        return step;
                    }
                    return {
                        ...step,
                        actions: (step.actions || []).map((recordedAction) => {
                            if (recordedAction.hid === action.payload.hid) {
                                return { ...recordedAction, targetDescription: action.payload.description };
                            }
                            return recordedAction;
                        }),
                    };
                });
            case 'set':
                return action.payload;
            // probably shouldn't ever use this one except for initial state
            default:
                return state;
        }
    };

    const { projectSlug, suiteSlug, storySlug } = useParams();
    const [localStorySlug, setLocalStorySlug] = useState<string | null>(null);

    const { data: suiteSettings } = useGet<TestSuite>(`/bff/project/${projectSlug}/suite/${suiteSlug}/settings`);
    const [isEditingABlock, setIsEditingABlock] = useState(false);
    const [steps, dispatch] = useReducer<(state: Step[], action: any) => Step[]>(stepReducer, []);

    const [queue, setQueue] = useState<{ index: number; parentIndex?: number }[]>([]);
    const [tempQueue, setTempQueue] = useState<{ index: number; parentIndex?: number }[]>([]);
    const [descriptionsBuffer, setDescriptionsBuffer] = useState<{ [key: string]: string }>({});
    const [highlighterCount, setHighlighterCount] = useState<number>(-1);
    const [highlightedSelectors, setHighlightedSelectors] = useState('');
    const [highlightedElement, setHighlightedElement] = useState<[]>([]);
    const [variables, setVariables] = useState<{ [key: string]: string }>({});
    const [precomputedVariables, setPrecomputedVariables] = useState<{ [key: string]: string }>({});
    const [isBlocksModalOpen, setIsBlocksModalOpen] = useState(false);

    const [mode, setMode] = useState<'record' | 'highlight'>('record');
    const [isPanelOpen, setIsPanelOpen] = useState(false);

    // Add a ref to track the socket
    const socketRef = useRef<Socket | null>(null);

    const [isPageListModalOpen, setIsPageListModalOpen] = useState(false);
    // Track which page UUIDs we've already shown errors for
    const [erroredPageUuids, setErroredPageUuids] = useState<Set<string>>(new Set());

    useEffect(() => {
        const separatorPairs = getSeparatorPairs(steps);
        let nextSteps = [...steps];
        if (separatorPairs.length > 0) {
            nextSteps = nextSteps.map((step) => ({ ...step, intentHash: null, intentPrompt: null }));
            for (const { intentHash, intentPrompt, pair } of separatorPairs) {
                for (let i = pair[0]; i <= pair[1]; ++i) {
                    nextSteps[i].intentHash = intentHash;
                    nextSteps[i].intentPrompt = intentPrompt;
                }
            }
        }

        nextSteps = groupByHash(nextSteps)
            // Remove groups that only contains separators
            .filter((group) => !group.items.every((item) => item.kind === 'separator'))
            .map((group) => {
                if (!group.intentHash || !group.intentPrompt) {
                    return group;
                }
                const first = group.items.at(0);
                const last = group.items.at(-1);
                // Empty group, leave as is
                if (first === undefined || last === undefined) {
                    return group;
                }
                // Make sure that every group is surrounded by separators
                return {
                    ...group,
                    items: [
                        ...(first.kind !== 'separator'
                            ? [createSeparatorStep(group.intentHash, group.intentPrompt)]
                            : []),
                        ...group.items,
                        ...(last.kind !== 'separator'
                            ? [createSeparatorStep(group.intentHash, group.intentPrompt)]
                            : []),
                    ],
                };
            })
            // Flatten
            .map((group) => group.items)
            .flat();

        if (JSON.stringify(nextSteps) !== JSON.stringify(steps)) {
            dispatch({
                type: 'set',
                payload: nextSteps,
            });
        }
    }, [steps]);

    const highlightSelectors = (selectors: any, force?: boolean) => {
        if (!selectors) {
            setHighlightedSelectors('');
            setHighlighterCount(-1);
            socketRef.current?.emit('highlightSelectors', { selectors: null });
        }
        const stringifiedSelectors = JSON.stringify(selectors);
        if (highlightedSelectors === stringifiedSelectors) {
            setHighlightedSelectors('');
            socketRef.current?.emit('highlightSelectors', { selectors: null });
        } else {
            setHighlightedSelectors(JSON.stringify(selectors));
            socketRef.current?.emit('highlightSelectors', { selectors, force });
        }
        return;
    };

    const [session, setSession] = useState<{
        runId: string;
        socket: Socket<DefaultEventsMap, DefaultEventsMap> | undefined;
        retryCount: number;
        status: string;
        isLoading: boolean;
        currentUrl?: string;
        debugPageId: string;
        pages: {
            uuid: string;
            variableName: string;
            url: string;
        }[];
    }>({
        runId: '',
        socket: undefined,
        retryCount: 0,
        status: '',
        isLoading: false,
        currentUrl: '',
        debugPageId: '',
        pages: [],
    });

    const { data: rawStoryToEdit } = useGet<Story>(
        storySlug ? `/bff/project/${projectSlug}/suite/${suiteSlug}/story/slug/${storySlug}` : null,
        { revalidateOnFocus: false },
    );

    const { data: lastRun } = useGet<Story>(
        storySlug ? `/bff/project/${projectSlug}/suite/${suiteSlug}/story/slug/${storySlug}/lastRun` : null,
        { revalidateOnFocus: false },
    );

    const alternateInstructionsWorkflow = !storySlug;

    const [localInstructions, setLocalInstructions] = useState<string | null>(null);

    const { data: instructions, mutate: mutateInstructions } = useGet<{ instructions: string }>(
        storySlug ? `/bff/project/${projectSlug}/suite/${suiteSlug}/story/slug/${storySlug}/instructions` : null,
    );

    const { data: blocksData } = useGet<Story[]>(`/bff/project/${projectSlug}/suite/${suiteSlug}/blocks`);

    const slug = `new-test-${generateSlug()}`;
    const formDefaultValue = {
        goal: isEditing ? rawStoryToEdit?.name || '' : slug,
        entryHref: 'https://',
        block: false,
    };

    if (suiteSettings?.defaultEntryHref) {
        formDefaultValue.entryHref = suiteSettings.defaultEntryHref;
    }

    if (rawStoryToEdit) {
        formDefaultValue.goal = `Copy of ${rawStoryToEdit.name}`;
        formDefaultValue.entryHref = rawStoryToEdit.entryHref;
    }

    if (isEditing && rawStoryToEdit) {
        formDefaultValue.goal = rawStoryToEdit.name;
    }

    const liveAgentForm = useForm<LiveAgentSchema>({
        defaultValues: formDefaultValue,
        resolver: zodResolver(liveAgentSchema),
        mode: 'onChange',
    });

    const liveGoal = liveAgentForm.watch('goal');

    useEffect(() => {
        if (liveGoal) {
            document.title = `${liveGoal} | Heal.dev`;
        }
    }, [liveGoal, session]);

    // Custom function to handle variables updates with preprocessing logic so we
    // dsiplay strings in the variables popover with a max length
    const updateVariables = (newVars: { [key: string]: string }) => {
        const formattedVariables: Record<string, string> = {};
        const maxLength = 150;

        // Helper function to truncate long strings
        const truncateValue = (val: string): string => {
            return val.length > maxLength ? `${val.substring(0, maxLength)}...` : val;
        };

        for (const [key, value] of Object.entries(newVars)) {
            if (typeof value === 'string' || typeof value === 'number') {
                formattedVariables[key] = truncateValue(String(value));
            } else {
                formattedVariables[key] = truncateValue(JSON.stringify(value));
            }
        }

        // Call the original setState function with processed variables
        setVariables((prev) => ({ ...prev, ...formattedVariables }));
    };

    // We disable editing variables from the front for now, they will be set in playwright steps
    const editVariables = undefined;
    // const editVariables = (action: 'add' | 'remove', payload: { [key: string]: string }) => {
    //     switch (action) {
    //         case 'add':
    //             updateVariables(payload);
    //             // remove the variable from the precomputed variables if it exists
    //             setPrecomputedVariables((prev) => {
    //                 const prevBuffer = { ...prev };
    //                 for (const key in payload) {
    //                     delete prevBuffer[key];
    //                 }
    //                 return prevBuffer;
    //             });
    //             break;
    //         case 'remove':
    //             setVariables((prev) => {
    //                 const newVariables = { ...prev };
    //                 for (const key in payload) {
    //                     delete newVariables[key];
    //                 }
    //                 return newVariables;
    //             });
    //             break;
    //         default:
    //             break;
    //     }
    // };
    const injectActionsFromLastRun = (step: Step, lastRunStep: Step) => {
        if (step.kind === 'aiAtomic') {
            // Make sure they target the same element so we do not inject wrong selectors
            // (should not happen, but just in case)
            if (step.target !== lastRunStep.target) {
                return;
            }
            // Inject the actions
            if (!step.actions) {
                step.actions = lastRunStep.actions;
            }
        }
    };

    const isEscapeHatch = (step: Step) =>
        step.kind === 'recordedActions' && step.targetingStrategy && step.targetingStrategy !== 'ai';

    useEffect(() => {
        if (suiteSettings?.defaultEntryHref) {
            liveAgentForm.setValue('entryHref', suiteSettings.defaultEntryHref);
        }
        if (rawStoryToEdit) {
            if (rawStoryToEdit.variables) {
                updateVariables(rawStoryToEdit.variables);
            }
            if (rawStoryToEdit.block) {
                setIsEditingABlock(true);
            }
            if (blocksData) {
                const storyToDuplicate = rawStoryToEdit;
                if (isEditing) {
                    liveAgentForm.setValue('goal', storyToDuplicate.name);
                } else {
                    liveAgentForm.setValue('goal', `Copy of ${storyToDuplicate.name}`);
                }
                liveAgentForm.setValue('entryHref', storyToDuplicate.entryHref);
                const initialSteps = [];
                let lastRunStepIndex = 0;
                for (let i = 0; i < storyToDuplicate.steps.length; ++i) {
                    const step = storyToDuplicate.steps[i];
                    let blockSteps = undefined;

                    if (step.kind === 'block') {
                        const block = blocksData.find((b) => !b.archived && b.slug === step.slug);
                        if (!block) return;
                        blockSteps = (block.steps || []).map((blockStep): any => {
                            return { ...blockStep, running: false, id: uuid() };
                        });

                        // Inject actions from last run. We need to retrieve the index of the step after the block in
                        // the last run in case the block was edited => we check blockStoryId
                        if (lastRun && lastRun.steps) {
                            let blockStoryId = -1;
                            for (let j = 0; j < blockSteps.length && lastRun.steps[j + lastRunStepIndex]; ++j) {
                                const currentBlockStoryId = lastRun.steps[j + lastRunStepIndex].blockStoryId;
                                if (blockStoryId === -1 && currentBlockStoryId) {
                                    blockStoryId = currentBlockStoryId;
                                }
                                if (currentBlockStoryId === blockStoryId) {
                                    const blockStep = blockSteps[j];
                                    // Do not overwrite selectors if it's an escape hatch
                                    if (!isEscapeHatch(blockStep)) {
                                        injectActionsFromLastRun(blockStep, lastRun.steps[j + lastRunStepIndex]);
                                    }
                                } else {
                                    break;
                                }
                            }

                            // Update lastRunStepIndex to the next step after the block
                            while (
                                lastRunStepIndex < lastRun.steps.length &&
                                lastRun.steps[lastRunStepIndex].blockStoryId === blockStoryId
                            ) {
                                lastRunStepIndex++;
                            }
                        }
                    }
                    // inject actions (with selectors, if any) from last run,
                    // so unchanged steps can still replay after editing the story
                    // Do not inject actions if it's an escape hatch
                    else if (!isEscapeHatch(step) && lastRun?.steps?.[lastRunStepIndex]) {
                        injectActionsFromLastRun(step, lastRun.steps[lastRunStepIndex]);
                        lastRunStepIndex++;
                    } else if (isEscapeHatch(step)) {
                        lastRunStepIndex++;
                    }

                    if (isEscapeHatch(step) && step.targetingStrategy && step.recorded) {
                        // rewrite recorded step as escape hatch action if it has targeting strategy
                        initialSteps.push({
                            ...step,
                            id: uuid(),
                            kind: 'aiAtomic',
                            action: step.recorded[0].name,
                            value: step.recorded[0].value,
                            intent: step.intent,
                            target: step.recorded[0].selectors[
                                getSelectorKeysFromEscapeHatch(step.targetingStrategy)[0]
                            ].selector,
                        });
                    } else {
                        initialSteps.push({
                            ...step,
                            blockSteps,
                            running: false,
                            id: uuid(),
                        });
                    }
                }
                dispatch({
                    type: 'set',
                    payload: initialSteps,
                });
            }
        }
    }, [rawStoryToEdit, blocksData, suiteSettings, lastRun]);

    // Handle common malformed URLs we have observed users input
    const sanitizeUrl = (s: string) => s.trim().replaceAll('https://https://', 'https://');

    const watchUrl = liveAgentForm.watch('entryHref');
    const watchTicketUrl = liveAgentForm.watch('ticket_url');

    useEffect(() => {
        liveAgentForm.setValue('entryHref', sanitizeUrl(watchUrl ?? ''));
        liveAgentForm.setValue('ticket_url', sanitizeUrl(watchTicketUrl ?? ''));
    }, [watchUrl, watchTicketUrl]);

    const navigate = useNavigate();

    const { trigger: triggerSession } = useMutate<LiveSession>('/bff/livesession/trigger', { method: 'POST' });

    const startSession = async () => {
        const entryHref = liveAgentForm.getValues().entryHref;
        if (!entryHref) {
            return;
        }
        if (entryHref === 'https://') {
            toast({
                title: `Please provide a valid URL`,
                variant: 'destructive',
            });
            return;
        }
        setSession((prev) => ({
            ...prev,
            isLoading: true,
            currentUrl: entryHref,
        }));
        try {
            const { runId: id } = (await triggerSession({ entryHref })) ?? {};
            if (!id) {
                throw new Error('Invalid session');
            }
            const socket = io(import.meta.env.VITE_CLUSTER_URL, {
                path:
                    process.env.NODE_ENV !== 'development' || import.meta.env.VITE_CLUSTER_SETUP === 'true'
                        ? `/${id}/node/agent`
                        : '/agent',
                extraHeaders: {
                    'x-heal-kind': 'studio',
                },
            });

            // Store in both state and ref
            socketRef.current = socket;
            setSession((prev) => ({
                ...prev,
                runId: id,
                socket,
                retryCount: 0,
                status: '',
                isLoading: false,
            }));
        } catch (err) {
            toast({
                title: `Error starting session`,
                description: formatError(err),
                variant: 'destructive',
            });
        }
    };

    useEffect(() => {
        if (onPremRunId) {
            const socket = io(import.meta.env.VITE_CLUSTER_URL, {
                path: `/${onPremRunId}/node/agent`,
                extraHeaders: {
                    'x-heal-kind': 'studio',
                },
            });
            liveAgentForm.setValue('entryHref', endpoint);
            if (storySlug) {
                if (editMode === 'clone') {
                    liveAgentForm.setValue('goal', `copy-of-${storySlug}`);
                } else {
                    liveAgentForm.setValue('goal', storySlug);
                }
            }

            setSession((prev) => ({
                ...prev,
                runId: onPremRunId,
                id: 'none',
                socket,
                retryCount: 0,
                isLoading: false,
                currentUrl: endpoint,
            }));
        }
    }, [onPremRunId, endpoint, storySlug]);

    useEffect(() => {
        if (session.socket) {
            session.socket.on('connect', () => {
                session.socket?.emit('startLiveSession', {
                    runId: session.runId,
                    orgId: -1,
                    entryHref: liveAgentForm.getValues().entryHref,
                    kind: 'LIVE',
                });
            });

            session.socket.on('status', ({ status, debugPageId }) => {
                setSession((prev) => ({
                    ...prev,
                    status,
                    debugPageId,
                }));
            });
        }

        return () => {
            if (session.socket) {
                session.socket.off('connect');
                session.socket.off('status');
                session.socket.close();
            }
        };
    }, [session.socket]);

    const { control } = liveAgentForm;

    // main socket drawer

    useEffect(() => {
        const { socket } = session;

        if (!socket) return;

        socket.on('stepUpdate', (data: { step: Step }) => {
            const runningStep = steps.find((step) => {
                if (step.kind === 'block') {
                    return step.blockSteps?.some((s) => s.running);
                }
                return step.running;
            });
            if (runningStep) {
                let parentIndex = undefined;
                let index = steps.findIndex((s) => s.id === runningStep.id);
                if (runningStep.kind === 'block') {
                    parentIndex = index;
                    index = (runningStep.blockSteps || []).findIndex((s) => s.running);
                }
                let nextVariables: { [key: string]: string } = { ...variables };
                const precomputedVariablesBuffer: { [key: string]: string } = { ...precomputedVariables };
                if (data.step.actions) {
                    const newVariables: { [key: string]: string } = {};
                    for (const action of data.step.actions) {
                        if (
                            data.step.kind === 'emailSignup' &&
                            data.step.saveCodeToVariable &&
                            action.extractedData &&
                            action.extractedData.code
                        ) {
                            newVariables[data.step.saveCodeToVariable] = `${action.extractedData.code}`;
                        }
                        if (action.variablesAfter && action.variablesBefore) {
                            for (const [key, value] of action.variablesAfter) {
                                const matchingVariableBefore = action.variablesBefore.find((v) => v[0] === key);
                                if (matchingVariableBefore && matchingVariableBefore[1] === value) {
                                    continue;
                                } else {
                                    delete precomputedVariablesBuffer[key];
                                    newVariables[key] = value;
                                }
                            }
                        }
                        if (action.variables) {
                            for (const key of Object.keys(action.variables)) {
                                if (
                                    action.kind === 'aiExtractInfo' ||
                                    action.kind === 'emailSignup' ||
                                    (action.kind === 'webAgent' && action.name === 'extractSelector')
                                ) {
                                    newVariables[key] = action.variables[key];
                                }
                                // if variables has an entry with the same key, we update it
                                // otherwise we discard it
                                // that way we don't store inline expressions in the variables space
                                if (variables[key]) {
                                    precomputedVariablesBuffer[key] = action.variables[key];
                                }
                            }
                        }
                    }
                    setPrecomputedVariables(precomputedVariablesBuffer);
                    nextVariables = { ...nextVariables, ...newVariables };
                    updateVariables(nextVariables);
                }
                dispatch({
                    type: 'update',
                    payload: {
                        index,
                        parentIndex,
                        data: {
                            result: data.step.result,
                            actions: data.step.actions,
                            childActions: data.step.childActions,
                            running: data.step.result === 'UNKNOWN',
                        },
                    },
                });
                if (data.step.result === 'FAIL' || data.step.result === 'CRASH') {
                    setQueue([]);
                }
                if (data.step.result === 'PASS' && queue.length > 0) {
                    const { parentIndex: nextParentIndex, index: nextIndex } = queue[0];
                    const nextStep =
                        nextParentIndex !== undefined
                            ? steps[nextParentIndex]?.blockSteps?.[nextIndex]
                            : steps[nextIndex];
                    if (nextStep) {
                        executeStep(nextStep, nextIndex, nextParentIndex, {
                            ...nextVariables,
                            ...precomputedVariablesBuffer,
                        });
                    } else {
                        setQueue([]);
                        resetQueue();
                    }
                }
            }
        });

        socket.on('recordedActions', (data: { actions: RecordedAction[] }) => {
            if (!data) {
                return;
            }
            const { actions } = data;
            if (actions && actions.length === 0) {
                return;
            }
            const runningStepIndex = steps.findIndex((step) => step.recording && step.kind === 'recordedActions');
            if (runningStepIndex !== -1) {
                const updatedData = actions.map((action) => {
                    const { hid } = action;
                    if (!hid) {
                        return action;
                    }
                    const targetDescription = descriptionsBuffer[hid];
                    return targetDescription ? { ...action, targetDescription } : action;
                });
                dispatch({
                    type: 'addAction',
                    payload: {
                        index: runningStepIndex,
                        data: updatedData,
                    },
                });
            }
        });

        socket.on('updateDescription', (data) => {
            if (!data) {
                return;
            }
            setDescriptionsBuffer((prev) => ({ ...prev, [data.hid]: data.description }));
            dispatch({
                type: 'addDescription',
                payload: { hid: data.hid, description: data.description },
            });
        });

        socket.on('navigatedTo', (data) => {
            setSession((prev) => ({
                ...prev,
                currentUrl: data.url,
            }));
        });

        socket.on('highlightElement', (data) => {
            setHighlightedElement(data.element);
        });

        socket.on('highlightCount', ({ count }) => {
            setHighlighterCount(count);
        });

        socket.on('pageChanged', ({ url, title, debugPageId }) => {
            setSession((prev) => ({
                ...prev,
                currentUrl: url,
                debugPageId,
            }));
        });

        socket.on('pageStoreChanged', ({ pages }: { pages: { uuid: string; variableName: string; url: string }[] }) => {
            // Check for pages without variable names
            pages.forEach((page) => {
                if (!page.variableName && !erroredPageUuids.has(page.uuid)) {
                    toast({
                        title: 'Unhandled page detected',
                        description: `A page was created without being assigned to a variable: ${page.url.substring(0, 50)}${page.url.length > 50 ? '...' : ''}`,
                        variant: 'destructive',
                        duration: 5 * 60 * 1000,
                    });
                    setErroredPageUuids((prev) => new Set(prev).add(page.uuid));
                }
            });

            setSession((prev) => ({
                ...prev,
                pages,
            }));
        });

        return () => {
            socket.off('stepUpdate');
            socket.off('recordedActions');
            socket.off('updateDescription');
            socket.off('navigatedTo');
            socket.off('highlightElement');
            socket.off('highlightCount');
            socket.off('pageChanged');
            socket.off('pageStoreChanged');
        };
    }, [session, steps, queue]);

    const computeQueue = (index: number, parentIndex?: number) => {
        if (isShiftKeyHeld) {
            return [{ index, parentIndex }];
        }
        const res: { index: number; parentIndex?: number }[] = [];

        const flattenedSteps: Array<Step & { index: number; parentIndex?: number }> = steps.flatMap((step, i) => {
            if (step.blockSteps) {
                return step.blockSteps.map((s, j) => {
                    return { ...s, parentIndex: i, index: j };
                });
            }
            return { ...step, index: i };
        });

        // find the index of the given step in the flattened steps
        const stepIndex = flattenedSteps.findIndex((step) => step.index === index && step.parentIndex === parentIndex);

        // if the step is not found, return an empty queue
        if (stepIndex === -1) return res;

        for (let i = stepIndex; i >= 0; i -= 1) {
            const step = flattenedSteps[i];
            if (step.kind === 'separator') {
                continue;
            }
            if (!step.executed && isStepExecutable(step)) {
                res.push({ index: step.index, parentIndex: step.parentIndex });
            } else {
                break;
            }
        }

        return res.reverse();
    };

    const computeAndSetTempQueue = (index: number, parentIndex?: number) => {
        const res = computeQueue(index, parentIndex);
        setTempQueue(res);
    };

    const resetQueue = () => {
        setTempQueue([]);
    };

    const postProcessStepForExecution = (step?: Step) => {
        if (!step) {
            return;
        }
        if (step.kind === 'recordedActions') {
            return step;
        }
        if (
            step.targetingStrategy === 'css' ||
            step.targetingStrategy === 'xpath' ||
            step.targetingStrategy === 'datatestid' ||
            step.targetingStrategy === 'arialabel' ||
            step.targetingStrategy === 'playwright'
        ) {
            return {
                ...step,
                kind: 'recordedActions',
                actions: getEscapeHatchActions(step),
                recorded: getEscapeHatchActions(step),
            };
        }
        return step;
    };

    const executeStep = (
        step: Step,
        index: number,
        parentIndex?: number,
        variablesBuffer: { [key: string]: string } = {},
    ) => {
        highlightSelectors(null);
        if (queue.length > 0) {
            queue.shift();
        }
        const socket = socketRef.current;
        if (!socket) {
            return;
        }

        const flattenedSteps: Array<Step & { index: number; parentIndex?: number }> = steps.flatMap(
            (flattenedStep, i) => {
                if (flattenedStep.blockSteps) {
                    return flattenedStep.blockSteps.map((s, j) => {
                        return { ...s, parentIndex: i, index: j };
                    });
                }
                return { ...flattenedStep, index: i };
            },
        );
        const currentIndex = flattenedSteps.findIndex((s) => s.index === index && s.parentIndex === parentIndex);
        const previousSteps = flattenedSteps.slice(0, currentIndex);

        const wereAllPreviousStepsExecuted = previousSteps.every((previousStep) => previousStep.executed);

        if (wereAllPreviousStepsExecuted || queue.length > 0) {
            dispatch({ type: 'update', payload: { index, parentIndex, data: { executed: true, running: true } } });
            socket.emit('executeSteps', {
                steps: [postProcessStepForExecution(step)],
                variables: { ...variables, ...variablesBuffer, ...precomputedVariables },
            });
        } else {
            const indices = computeQueue(index, parentIndex);
            const [first, ...rest] = indices;
            setQueue(rest);
            const { parentIndex: firstParentIndex, index: firstIndex } = first;
            dispatch({
                type: 'update',
                payload: { index: firstIndex, parentIndex: firstParentIndex, data: { executed: true, running: true } },
            });

            const stepToExecute =
                firstParentIndex !== undefined ? steps[firstParentIndex]?.blockSteps?.[firstIndex] : steps[firstIndex];
            socket.emit('executeSteps', {
                steps: [postProcessStepForExecution(stepToExecute)],
                variables: { ...variables, ...variablesBuffer, ...precomputedVariables },
            });
        }
    };

    const removeStep = (step: Step, index: number, parentIndex?: number) => {
        if (step.running) {
            session.socket?.emit('abortStep', {});
        }
        dispatch({ type: 'remove', payload: { index, parentIndex } });
    };

    const { trigger: triggerCreate } = useMutate<Story>(
        () => {
            return `/bff/project/${projectSlug}/suite/${suiteSlug}/story`;
        },
        {
            revalidate: true,
            method: 'POST',
        },
    );

    const { trigger: triggerEdit } = useMutate<Story>(
        () => {
            return `/bff/project/${projectSlug}/suite/${suiteSlug}/story/${storySlug ?? localStorySlug}`;
        },
        {
            revalidate: true,
            method: 'PUT',
        },
    );

    const { trigger: updateInstruction } = useMutate(
        `/bff/project/${projectSlug}/suite/${suiteSlug}/story/:id/instructions`,
        {
            revalidate: true,
            method: 'POST',
        },
    );

    const publish = async (asBlock?: boolean, duplicate?: boolean, stay?: boolean) => {
        if (!liveAgentForm.getValues().goal || liveAgentForm.getValues().goal.length === 0) {
            toast({
                title: 'Please provide a name for the story',
                variant: 'destructive',
            });
            return;
        }
        if (!drafting) {
            if (
                (shouldCheckName && nameIsAvailable && nameIsAvailable?.available === false && !isEditing) ||
                (shouldCheckName &&
                    nameIsAvailable &&
                    nameIsAvailable?.available === false &&
                    isEditing &&
                    rawStoryToEdit?.name !== liveAgentForm.getValues().goal) // if editing, the name is allowed to be the same to the original
            ) {
                toast({
                    title: 'This name already exists',
                    variant: 'destructive',
                });
                return;
            }
        }

        // If we are on the editor, make sure the syntax is valid
        if (editorRef.current?.isSyntaxValid && !editorRef.current.isSyntaxValid()) {
            toast({
                title: 'Syntax error in the code editor, please fix it before publishing',
                variant: 'destructive',
            });
            return;
        }

        // Make sure we have the latest steps if we are editing
        let latestSteps = steps;
        if (editorRef.current?.getCurrentSteps) {
            latestSteps = editorRef.current.getCurrentSteps();
            dispatch({ type: 'set', payload: latestSteps });
        }

        const { goal, entryHref, ticket_url } = liveAgentForm.getValues();
        let name = goal;
        if (duplicate) {
            name = `${goal} (copy ${Math.random().toString(36).substring(8)})`;
        }
        const story = {
            name,
            kind: 'FUNCTIONAL',
            ticketUrl: ticket_url,
            liveRunId: session.runId.replace('live-', ''),
            steps: latestSteps
                .filter((step) => step.kind !== 'separator')
                .map((step) => {
                    // note: even though "escape hatch" steps are persisted as "recordedActions" in the back-end,
                    // the front-end consider them as 'aiAtomicStep', internally.
                    if (step.kind === 'recordedActions') {
                        const trimmedActions = trimHovers(step.actions || step.recorded || []);
                        return {
                            ...step,
                            executed: false,
                            running: false,
                            recording: false,
                            actions: trimmedActions,
                            recorded: trimmedActions.map((action) => {
                                if (action.name === 'hover') {
                                    return {
                                        ...action,
                                        blocking: false,
                                        // make hovers non-blocking on publish
                                        // not very clean but it works
                                    };
                                }
                                return action;
                            }),
                        };
                    }
                    // "escape hatch" steps are persisted as 'recordedActions', in the back-end
                    if (step.targetingStrategy && step.targetingStrategy !== 'ai') {
                        return {
                            kind: 'recordedActions',
                            executed: false,
                            running: false,
                            recording: false,
                            intent: step.intent,
                            targetingStrategy: step.targetingStrategy,
                            actions: trimHovers(step.actions || []),
                            recorded:
                                getEscapeHatchActions(step).map((action) => {
                                    return { ...action, targetingStrategy: step.targetingStrategy };
                                }) || [],
                            code: step.code,
                        };
                    }
                    return { ...step, executed: false, running: false, recording: false };
                }),
            variables,
            browsers: {
                names: ['chromium'],
                viewport: {
                    width: 1280,
                    height: 720,
                },
            },
            entryHref,
            featureFlags: rawStoryToEdit?.featureFlags,
            block: asBlock,
        };
        let endpointTrigger = duplicate || !isEditing ? triggerCreate : triggerEdit;
        if (drafting) {
            endpointTrigger = triggerEdit;
        }

        const res = await endpointTrigger(story);
        if (!res) return;
        if (res.slug && alternateInstructionsWorkflow && localInstructions) {
            try {
                await updateInstruction({
                    idParam: res.slug,
                    instructions: localInstructions,
                });
            } catch (err) {
                toast({
                    title: 'Error updating instructions',
                    description: formatError(err),
                    variant: 'destructive',
                });
            }
        }
        if (stay) {
            setDrafting(true);
            setLocalStorySlug(res.slug);
            toast({
                title: 'Changes saved successfully',
                duration: 2000,
            });
            return;
        }
        session.socket && session.socket.emit('endSession');
        const redirectUrl = `/suite/${projectSlug}/${suiteSlug}/${asBlock ? 'blocks' : 'stories'}`;
        // @ts-ignore
        navigate(`${redirectUrl}?highlightStory=${res.id}`);
    };

    const flatSteps = steps.flatMap((step) => {
        if (step.blockSteps) {
            return step.blockSteps;
        }
        return step;
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [shouldCheckName, setShouldCheckName] = useState(false);

    const { data: nameIsAvailable } = useGet<any>(
        shouldCheckName && liveAgentForm.getValues().goal && liveAgentForm.getValues().goal.length > 0
            ? `/bff/project/${projectSlug}/suite/${suiteSlug}/story/${encodeURIComponent(liveAgentForm.getValues().goal)}/check`
            : null,
        { method: 'GET' },
    );

    useEffect(() => {
        // Check name availability whenever the 'goal' input is set, including the default value
        if (liveAgentForm.getValues().goal && liveAgentForm.getValues().goal.length > 0) {
            setShouldCheckName(true);
        }
    }, []);

    useEffect(() => {
        if (drafting) {
            return;
        }
        if (
            (shouldCheckName && nameIsAvailable && nameIsAvailable?.available === false && !isEditing) ||
            (shouldCheckName &&
                nameIsAvailable &&
                nameIsAvailable?.available === false &&
                isEditing &&
                rawStoryToEdit?.name !== liveAgentForm.getValues().goal) // if editing, the name is allowed to be the same to the original
        ) {
            setErrorMessage('This name already exists.');
        } else {
            setErrorMessage('');
        }
    }, [nameIsAvailable, shouldCheckName, drafting, liveAgentForm.getValues().goal]);

    // debounce time to not dos the server
    const handleInputChange = useCallback(
        debounce(() => {
            if (liveAgentForm.getValues().goal && liveAgentForm.getValues().goal.length > 0) {
                setShouldCheckName(true);
            }
        }, 300), // 300ms debounce time
        [],
    );

    const isURLValid = (url: string) => {
        try {
            // check that it's parsable by the URL constructor and, if it is,
            // if the hostname does not contain commas or spaces
            return new URL(url).hostname.match(/[\s,]/) === null;
        } catch {
            return false;
        }
    };

    const abortCurrentRunningStep = () => {
        if (flatSteps.some((step) => step.running)) {
            session.socket?.emit('abortStep', {});
            Promise.race([
                new Promise((_, reject) => {
                    setTimeout(() => {
                        reject(new Error('Timeout!'));
                    }, 1000); // 1 second timeout
                }),
                new Promise<void>((resolve, reject) => {
                    session.socket?.once('abortReceived', () => {
                        resolve();
                    });
                }),
            ]).finally(() => {
                setQueue([]);
                dispatch({
                    type: 'set',
                    payload: steps.map((step) => {
                        if (step.running) {
                            return {
                                ...step,
                                running: false,
                            };
                        }
                        return step;
                    }),
                });
            });
        }
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                abortCurrentRunningStep();
            }
            if (e.key === 'Shift') {
                setIsShiftKeyHeld(true);
            }
            if ((e.metaKey || e.ctrlKey) && e.key === 's') {
                e.preventDefault();
                publish(false, false, true);
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'Shift') {
                setIsShiftKeyHeld(false);
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [flatSteps]);

    const leftPaneRef = useRef<HTMLDivElement>(null);

    const [hidePlaywright, setHidePlaywright] = useState(false);
    const toggleHidePlaywright = () => setHidePlaywright(!hidePlaywright);

    const videoTab = (
        <Split
            className="flex flex-col h-full overflow-hidden"
            sizes={[80, 20]}
            gutterSize={4}
            key="right-pane"
            gutterStyle={(_dimension, gutterSize) => {
                return { height: `${gutterSize}px`, opacity: '0' };
            }}
            direction="vertical"
        >
            <div id="top-pane" className={cn('pe-4 flex flex-col', isPanelOpen && '!h-full')}>
                {session.socket ? (
                    <>
                        {session.runId && session.debugPageId && session.status === 'sessionReady' ? (
                            <>
                                <div className="flex flex-row gap-2 items-center justify-between pb-2">
                                    <div className="flex flex-row items-center gap-2 truncate w-full text-slate-500">
                                        <button
                                            onClick={() => {
                                                session.socket?.emit('goBack', {});
                                            }}
                                            disabled={flatSteps.some((step) => step.running || step.recording)}
                                        >
                                            <ArrowLeft />
                                        </button>
                                        {!isPanelOpen && (
                                            <button
                                                aria-label="Toggle highlighter mode"
                                                title="Toggle highlighter mode"
                                                onClick={() => {
                                                    if (mode === 'record') {
                                                        setHighlightedElement([]);
                                                        setMode('highlight');
                                                    } else {
                                                        setHighlightedElement([]);
                                                        setMode('record');
                                                    }
                                                }}
                                            >
                                                <SquareDashedMousePointer
                                                    className={mode === 'highlight' ? 'text-blue-500' : ''}
                                                />
                                            </button>
                                        )}
                                        <button
                                            aria-label="Toggle devtools Elements panel"
                                            title="Toggle devtools Elements panel"
                                            onClick={() => {
                                                setIsPanelOpen((prev) => !prev);
                                            }}
                                        >
                                            <PanelRight className={isPanelOpen ? 'text-blue-500' : ''} />
                                        </button>
                                        <button
                                            aria-label="Show page list"
                                            title="Show page list"
                                            onClick={() => {
                                                setIsPageListModalOpen(true);
                                            }}
                                        >
                                            <Files className="text-slate-500" />
                                        </button>
                                        <p className="text-small w-full p-2 bg-slate-100 rounded truncate">
                                            {session.currentUrl}
                                        </p>
                                    </div>
                                    {flatSteps.some((s) => s.recording) ? (
                                        <div className="flex flex-row items-center gap-1 text-xs text-gray-700">
                                            recording...
                                        </div>
                                    ) : (
                                        ' '
                                    )}{' '}
                                    {flatSteps.some((s) => s.running) ? (
                                        <div className={'flex flex-row items-center gap-1 py-1 text-xs text-gray-700'}>
                                            running...
                                            <button
                                                className="h-6 px-2 font-normal text-sm"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    abortCurrentRunningStep();
                                                }}
                                            >
                                                stop
                                            </button>
                                        </div>
                                    ) : (
                                        ' '
                                    )}
                                </div>
                                <LiveAgentPlayer
                                    runId={session.runId}
                                    debugPageId={session.debugPageId}
                                    socket={session.socket}
                                    isRecording={steps.every((step) => !step.running)}
                                    mode={mode}
                                    panelOpen={isPanelOpen}
                                />
                            </>
                        ) : (
                            <div
                                style={{ aspectRatio: 1280 / 720 }}
                                className="w-full flex max-h-full flex-col items-center justify-center bg-slate-50"
                            >
                                <p className="animate-pulse">Starting session…</p>
                                <p className="animate-pulse text-xs">This usually takes 20s</p>
                            </div>
                        )}
                    </>
                ) : (
                    <div
                        style={{ aspectRatio: 1280 / 720 }}
                        className="w-full flex max-h-full flex-col items-center justify-center bg-slate-50"
                    >
                        <p>Click GO to start editing your story</p>
                    </div>
                )}
            </div>
            {!isPanelOpen && (
                <div id="bottom-pane">
                    {' '}
                    {highlighterCount !== -1 && <div>{getHighlighterCountMessage(highlighterCount)}</div>}
                    {mode === 'highlight' && (
                        <div>
                            {highlightedElement.map(({ type, selector, code }) => {
                                if (selector) {
                                    return (
                                        <div className="flex items-center px-2" key={type}>
                                            <span className="truncate">
                                                <pre className="text-xs rounded border text-purple-600 inline px-1">
                                                    :{type}
                                                </pre>
                                                <span
                                                    onClick={() => {
                                                        toast({
                                                            title: 'Copied to clipboard',
                                                            duration: 500,
                                                        });
                                                        navigator.clipboard.writeText(code ?? selector);
                                                    }}
                                                    className="text-xs px-1 truncate cursor-pointer"
                                                >
                                                    {code ?? selector}
                                                </span>
                                            </span>
                                            <button
                                                onClick={() => {
                                                    toast({
                                                        title: 'Copied to clipboard',
                                                        duration: 500,
                                                    });
                                                    navigator.clipboard.writeText(code ?? selector);
                                                }}
                                                className="ms-auto px-2 py-1 hover:bg-purple-100 rounded"
                                            >
                                                <Copy size={14} />
                                            </button>
                                        </div>
                                    );
                                }
                            })}
                        </div>
                    )}
                </div>
            )}
        </Split>
    );

    const tabItems = [
        {
            title: 'Replay',
            tabHeader: (
                <div className="flex gap-[8px] py-2 px-[8px] items-center">
                    <Videotape size="16" className="text-purple-600" />
                    Live view
                </div>
            ),
            content: <>{videoTab}</>,
        },
        {
            title: 'Instructions',
            tabHeader: (
                <div className="flex gap-[8px] py-2 px-[8px] items-center">
                    <FileText size="16" className="text-purple-600" />
                    Instructions
                </div>
            ),
            content: (
                <div className="bg-white rounded-lg h-full overflow-auto p-4">
                    <InstructionsTab
                        storySlug={storySlug}
                        instructionsData={instructions || { instructions: localInstructions ?? '' }}
                        onSave={(draft) => {
                            if (alternateInstructionsWorkflow) {
                                setLocalInstructions(draft);
                            }
                            mutateInstructions();
                        }}
                    />
                </div>
            ),
        },
    ];

    // Handle switching between views
    const handleViewModeChange = () => {
        if (viewMode === 'list') {
            // Switching from list to editor view
            setViewMode('editor');
        } else {
            // If we are on the editor, make sure the syntax is valid
            if (editorRef.current?.isSyntaxValid && !editorRef.current.isSyntaxValid()) {
                toast({
                    title: 'Syntax error in the code editor, please fix it before switching to list view',
                    variant: 'destructive',
                });
                return;
            }

            setViewMode('list');
            const newSteps = editorRef.current?.getCurrentSteps();
            if (newSteps) {
                dispatch({
                    type: 'set',
                    payload: newSteps,
                });
            }
        }
    };

    return (
        <ErrorBoundary steps={steps}>
            <div
                className="flex flex-col h-full overflow-hidden rounded-[10px] bg-white mx-[6px] drop-shadow-[0_0_10px_rgba(0,0,0,0.05)]"
                id="live-agent-session"
            >
                <Form {...liveAgentForm}>
                    <div className="flex flex-row justify-between py-2 border-b mb-2 mx-4" key="info-actions">
                        <div className="text-h4 flex items-center w-full flex-wrap">
                            <label className="flex items-center">
                                <BookOpen className="text-slate-700 me-[6px]" />
                                <span className="flex items-center gap-x-1 text-slate-500">
                                    <span>{suiteSettings?.name}</span>
                                    <span className="text-slate-300 text-h3">/</span>
                                    <FormField
                                        control={control}
                                        name="goal"
                                        render={({ field }) => (
                                            <FormItem className="inline">
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        title="Story name"
                                                        aria-label="Story name"
                                                        id="goal"
                                                        autoFocus
                                                        variant="none"
                                                        className="pe-1"
                                                        disabled={isEditingABlock}
                                                        onInput={(e) => {
                                                            setErrorMessage('');
                                                            setShouldCheckName(false);
                                                            field.onChange(e);
                                                            handleInputChange();
                                                        }}
                                                        style={
                                                            {
                                                                fieldSizing: 'content',
                                                            } as CSSProperties
                                                        }
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />
                                </span>
                                {!isEditingABlock && <Edit2 className="text-slate-500" width="14px" height="14px" />}
                                {errorMessage && <p className="text-red-500 text-sm font-medium">{errorMessage}</p>}
                            </label>
                            <div className="flex gap-x-2 items-center justify-end ms-auto">
                                <button className="shrink-0" onClick={toggleHidePlaywright}>
                                    <img
                                        className={cn(
                                            'w-8 transition-[filter] duration-300',
                                            hidePlaywright && 'force-greyOut',
                                        )}
                                        src="/playwright_logo.svg"
                                        alt="Toggle Playwright"
                                    />
                                    <span className="sr-only">{`${hidePlaywright ? 'Show' : 'Hide'} Playwright selectors`}</span>
                                </button>
                                <Button
                                    onClick={handleViewModeChange}
                                    size="small"
                                    color="slate"
                                    disabled={steps.some((step) => step.kind === 'block')}
                                >
                                    {viewMode === 'list' ? 'Code View' : 'List View'}
                                </Button>
                                {session.socket && (
                                    <LiveAgentRunInformationPopover>
                                        <div className="flex gap-x-1 items-center px-4">
                                            <Link2 className="text-purple-500" size="16px" />
                                            Entry URL: {liveAgentForm.getValues().entryHref}
                                        </div>
                                        <div className="flex gap-x-1 items-center px-4">
                                            <MonitorCog className="text-purple-500" size="16px" />
                                            Run ID:{' '}
                                            <Link
                                                to={`https://telemetry.betterstack.com/team/220949/tail?q=runId%3D${session.runId}`}
                                                target="_blank"
                                            >
                                                {session.runId}
                                            </Link>
                                        </div>
                                    </LiveAgentRunInformationPopover>
                                )}
                                {isEditingABlock ? (
                                    <>
                                        <Button
                                            onClick={() => publish(true)}
                                            size="small"
                                            color="slate"
                                            dropdown={[
                                                {
                                                    label: 'Save changes',
                                                    icon: <Save size="16" />,
                                                    onClick: () => publish(true),
                                                },
                                                {
                                                    label: 'Save without redirect',
                                                    icon: <Save size="16" />,
                                                    onClick: () => publish(true, false, true),
                                                },
                                                {
                                                    label: 'Save as copy',
                                                    icon: <Copy size="16" />,
                                                    onClick: () => publish(true, true),
                                                },
                                            ]}
                                        >
                                            Save changes
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        {steps.some((step) => step.kind === 'block') ? (
                                            <TooltipOverParent
                                                renderTooltip={() => (
                                                    <p>Impossible to save a story containing blocks as a Block</p>
                                                )}
                                                renderTrigger={() => (
                                                    <Button size="small" color="slate" disabled>
                                                        Save as block
                                                    </Button>
                                                )}
                                            />
                                        ) : (
                                            <Button
                                                onClick={() => publish(true)}
                                                size="small"
                                                color={blockWorkflow ? 'purple' : 'slate'}
                                            >
                                                Save as block
                                            </Button>
                                        )}

                                        {!blockWorkflow && (
                                            <Button
                                                dropdown={[
                                                    {
                                                        label: isEditing ? 'Save changes' : 'Publish',
                                                        icon: <Save size="16" />,
                                                        onClick: () => publish(),
                                                    },
                                                    {
                                                        label: 'Save without redirect',
                                                        icon: <Save size="16" />,
                                                        onClick: () => publish(false, false, true),
                                                    },
                                                    {
                                                        label: 'Save as copy',
                                                        icon: <Copy size="16" />,
                                                        onClick: () => publish(false, true),
                                                    },
                                                ]}
                                                onClick={() => publish()}
                                                size="small"
                                                color="purple"
                                            >
                                                {isEditing ? 'Save changes' : 'Publish'}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <Split
                        className="split h-full overflow-hidden"
                        gutterSize={4}
                        sizes={[40, 60]}
                        key="viewer"
                        gutterStyle={(_dimension, gutterSize) => {
                            return { width: `${gutterSize}px`, opacity: '0' };
                        }}
                    >
                        <div
                            id="left-pane"
                            className={cn('flex flex-col h-full', onPremRunId && '!w-full')}
                            ref={leftPaneRef}
                        >
                            {!session.socket && (
                                <div className="px-4 pb-2">
                                    <form
                                        onSubmit={(e) => {
                                            e.preventDefault();
                                            startSession();
                                        }}
                                    >
                                        <label htmlFor="entryHref" className="block text-md font-medium mb-2">
                                            Test entrypoint
                                        </label>
                                        <div className="flex gap-x-2">
                                            <FormField
                                                control={control}
                                                name="entryHref"
                                                render={({ field }) => (
                                                    <FormItem className="w-full">
                                                        <FormControl>
                                                            <Input
                                                                {...field}
                                                                variant="none"
                                                                className="rounded w-full border border-slate-300 p-1 flex-1"
                                                                placeholder="Enter a URL"
                                                            />
                                                        </FormControl>
                                                        <FormMessage>
                                                            {!isURLValid(liveAgentForm.getValues().entryHref) &&
                                                                liveAgentForm.getFieldState('entryHref').isDirty &&
                                                                'The URL format is invalid. Make sure to enter a valid URL'}
                                                        </FormMessage>
                                                    </FormItem>
                                                )}
                                            />
                                            <button
                                                type="submit"
                                                className="rounded bg-blue-500 text-white px-4 py-1 flex gap-x-2 items-center self-start h-[34px] disabled:opacity-50 disabled:cursor-not-allowed"
                                                disabled={
                                                    session.isLoading ||
                                                    liveAgentForm.getFieldState('entryHref').invalid ||
                                                    !isURLValid(liveAgentForm.getValues().entryHref)
                                                }
                                            >
                                                GO
                                                {session.isLoading && <Spinner />}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                            {steps.length > 0 && viewMode === 'list' && (
                                <div className="px-4 overflow-y-auto pb-6 h-full scrollbar-hide">
                                    <LiveAgentSteps
                                        steps={steps}
                                        dispatch={dispatch}
                                        executeStep={executeStep}
                                        removeStep={removeStep}
                                        highlightSelectors={highlightSelectors}
                                        setQueue={computeAndSetTempQueue}
                                        resetQueue={resetQueue}
                                        queue={queue[0] ? queue : tempQueue}
                                        isDisabled={!onPremRunId && session.status !== 'sessionReady'}
                                        variables={variables}
                                        precomputedVariables={precomputedVariables}
                                        editVariables={editVariables}
                                        openStep={openStep}
                                        updateOpenStep={updateOpenStep}
                                        fullScreenContainer={leftPaneRef}
                                        hidePlaywright={hidePlaywright}
                                    />
                                </div>
                            )}
                            {viewMode === 'editor' && (
                                <LiveAgentEditor
                                    steps={steps}
                                    height="100%"
                                    ref={editorRef}
                                    executeStep={executeStep}
                                    dispatch={dispatch}
                                    queue={queue}
                                    isDisabled={!onPremRunId && session.status !== 'sessionReady'}
                                    hidePlaywright={hidePlaywright}
                                    variables={variables}
                                    highlightSelectors={highlightSelectors}
                                    className="overflow-y-auto rounded-tl-[10px] h-full scrollbar-hide"
                                />
                            )}
                            {onPremRunId && flatSteps.some((step) => step.running) && (
                                <div className="w-full flex items-center justify-center py-1">
                                    <div className={'flex flex-row items-center gap-1 py-1 text-gray-700'}>
                                        The step is running...
                                        <button
                                            className="px-2 py-1 rounded bg-red-500 text-white font-normal"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                abortCurrentRunningStep();
                                            }}
                                        >
                                            Stop
                                        </button>
                                    </div>
                                </div>
                            )}
                            {session.socket && viewMode === 'list' && (
                                <LiveAgentRemoteControl
                                    dispatch={dispatch}
                                    socket={session.socket}
                                    steps={steps}
                                    isDisabled={
                                        flatSteps.some((step) => step.running) || session.status !== 'sessionReady'
                                    }
                                    openBlocksModal={
                                        blocksData && blocksData?.length > 0 && !isEditingABlock
                                            ? () => setIsBlocksModalOpen(true)
                                            : undefined
                                    }
                                />
                            )}
                        </div>
                        {!onPremRunId && (
                            <div id="right-pane">
                                <Tabs items={tabItems} />
                            </div>
                        )}
                    </Split>
                </Form>
                {isBlocksModalOpen && (
                    <Modal
                        isOpen={isBlocksModalOpen}
                        onClose={() => setIsBlocksModalOpen(false)}
                        title="Choose a block to insert in the story"
                    >
                        <div>
                            <div className="grid grid-cols-2 gap-4">
                                {blocksData
                                    ?.filter((block) => block.archived === false)
                                    .map((block) => (
                                        <LiveAgentBlockCard
                                            key={block.id}
                                            block={block}
                                            dispatch={dispatch}
                                            closeModal={() => setIsBlocksModalOpen(false)}
                                        />
                                    ))}
                            </div>
                        </div>
                    </Modal>
                )}
                {isPageListModalOpen && (
                    <Modal isOpen={isPageListModalOpen} onClose={() => setIsPageListModalOpen(false)} title="Page List">
                        <div className="overflow-auto max-h-96">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-100">
                                    <tr>
                                        <th className="px-4 py-2 text-left">Variable Name</th>
                                        <th className="px-4 py-2 text-left">URL</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {session.pages.length > 0 ? (
                                        session.pages.map((page, index) => (
                                            <tr key={index} className="border-t border-slate-200">
                                                <td className="px-4 py-2 font-mono text-xs">{page.variableName}</td>
                                                <td className="px-4 py-2 truncate max-w-md">{page.url}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan={2} className="px-4 py-2 text-center text-slate-500">
                                                No pages available
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </Modal>
                )}
            </div>
        </ErrorBoundary>
    );
};
